import classNames from 'classnames'

import {
  STOCKTAKE_BADGES,
  STOCKTAKE_LINE_TOTAL,
} from '../../common/constants/Stocktakes.js'
import {Plural, PluralBlock} from '../../common/components/Plural.js'
import FormattedDate from '../../common/components/FormattedDate.js'
import RowAddress from '../../common/components/List/RowAddress.js'
import Checkbox from '../../common/components/Checkbox.js'
import ButtonLink from '../../common/components/Button/ButtonLink.js'
import ButtonPrimary from '../../common/components/Button/ButtonPrimary.js'
import TimelinePopover from '../../common/components/List/TimelinePopover/index.js'
import Quantity from '../../common/components/Quantity.js'
import {
  warehousesSelector,
  getWarehouseName,
  getWarehouseAddress,
} from '../../data/warehouses.js'
import {
  createStocktakeSelector,
  stocktakeStatusSelector,
  createStocktakeCommentGroupsSelector,
  createStocktakeCreatedDateSelector,
  createStocktakeUpdatedDateSelector,
  createStocktakeTagsSelector,
  stocktakeWarehouseIDSelector,
  internalNotesSelector,
  lineCountSelector,
  lineCountListSelector,
  canStocktakeLabelSelector,
  ensureStocktakeComments,
} from '../../data/stocktakes.js'
import {
  stocktakeListSelectReferenceID,
  stocktakeListToggleExpanded,
  createIsSelectedSelector,
  createIsExpandedSelector,
  showStocktakeStatusBadge,
} from './stocktakeListFunctions.js'
import StocktakeTags from './StocktakeTags.js'
import {useSelector} from '../../store.js'
import {useMemo} from 'react'
import {showStocktakeInternalNotesModal} from './Modals/EditStocktakeParamModal.js'
import {showStocktakeModal} from './Modals/StocktakeModal.js'
import {stocktakeDetailURLSelector} from '../StocktakeDetailPage/stocktakeDetailFunctions.js'
import Icon from '../../common/icons/Icon.js'

/**
 * Stocktake Row component for displaying a single Manufacturing Order (Stocktake) in the list.
 *
 * @param {Object} props - The component props.
 * @param {String} props.referenceID - The reference ID of the Stocktake.
 *
 * @returns {React.ReactElement} The rendered component.
 */
export default function Row({referenceID}) {
  const [
    commentGroupsSelector,
    isSelectedSelector,
    isExpandedSelector,
    createdDateSelector,
    updatedDateSelector,
    tagsSelector,
  ] = useMemo(() => {
    const stocktakeSelector = createStocktakeSelector(referenceID)

    return [
      createStocktakeCommentGroupsSelector(stocktakeSelector),
      createIsSelectedSelector(referenceID),
      createIsExpandedSelector(referenceID),
      createStocktakeCreatedDateSelector(stocktakeSelector),
      createStocktakeUpdatedDateSelector(stocktakeSelector),
      createStocktakeTagsSelector(stocktakeSelector),
    ]
  }, [referenceID])

  const showBadge = useSelector(showStocktakeStatusBadge)
  const isSelected = useSelector(isSelectedSelector)
  const isExpanded = useSelector(isExpandedSelector)
  const createdDate = useSelector(createdDateSelector)
  const updatedDate = useSelector(updatedDateSelector)
  const tags = useSelector(tagsSelector)
  const warehousesByID = useSelector(warehousesSelector)
  const internalNotes = useSelector((state) =>
    internalNotesSelector(state, {referenceID}),
  )
  const stocktakeStatus = useSelector((state) =>
    stocktakeStatusSelector(state, {referenceID}),
  )
  const badge = STOCKTAKE_BADGES[stocktakeStatus]
  const warehouseID = useSelector((state) =>
    stocktakeWarehouseIDSelector(state, {referenceID}),
  )

  const lineCount = useSelector((state) =>
    lineCountSelector(state, {referenceID}),
  )
  const lineCountList = useSelector((state) =>
    lineCountListSelector(state, {referenceID}),
  )
  const canStocktakeLabel = useSelector((state) =>
    canStocktakeLabelSelector(state, {referenceID}),
  )
  const detailURL = useSelector((state) =>
    stocktakeDetailURLSelector(state, {referenceID}),
  )

  const singleSKUStatusName =
    lineCountList.length === 1 ? lineCountList[0].display : ''

  const skuCount = lineCount[STOCKTAKE_LINE_TOTAL] || 0
  const warehouse = warehousesByID[warehouseID]

  return (
    <tr
      className={classNames('table__tr table__tr--stocktake-row', {
        'table__tr--expanded': isExpanded,
      })}
    >
      <td className="table__td table__td--order-checkbox relative">
        <TimelinePopover
          title="Stocktake Timeline"
          type="stocktake"
          commentGroupsSelector={commentGroupsSelector}
          ensureComments={() => ensureStocktakeComments(referenceID)}
        />
        <Checkbox
          checked={isSelected}
          onClick={(checked, event) =>
            stocktakeListSelectReferenceID(referenceID, checked, event.shiftKey)
          }
        />
      </td>
      <td className="table__td">
        {showBadge && (
          <div
            className="status-badge inline-block lh-md tooltip--top margin-right-5 v-align-top"
            data-hint={stocktakeStatus}
          >
            <strong>{badge}</strong>
          </div>
        )}
        <div className="wrap--product-name inline-block">
          <a
            className="btn btn--link darkest fs-n0 lh-md margin-right-5"
            href={detailURL}
          >
            <strong>{referenceID}</strong>
          </a>
        </div>
        {tags.length > 0 && (
          <div
            className={
              isExpanded
                ? 'wrap--po-addl-info list list--order-data'
                : 'wrap--summary-tags-table wrap--summary-tags-inline'
            }
          >
            <StocktakeTags referenceID={referenceID} tags={tags} />
          </div>
        )}
        {canStocktakeLabel && (
          <div className="margin-top-5">
            <ButtonPrimary
              size="xx-sm"
              isOutlined
              onClick={() => showStocktakeModal({referenceID})}
            >
              {canStocktakeLabel}
            </ButtonPrimary>
          </div>
        )}
        <dl className="wrap--po-addl-info list list--order-data with-top-divider">
          <dt className="list__title--order-data">Notes</dt>
          {internalNotes ? (
            <dd className="list__item--order-data">
              <div className="ws-pre-wrap">{internalNotes}</div>
              <ButtonLink
                className="meta-stocktakelist-button-edit-internal-note"
                onClick={() => showStocktakeInternalNotesModal(referenceID)}
              >
                Edit
              </ButtonLink>
            </dd>
          ) : (
            <dd className="list__item--order-data">
              <ButtonLink
                className="meta-stocktakelist-button-add-internal-note"
                onClick={() => showStocktakeInternalNotesModal(referenceID)}
              >
                Add note
              </ButtonLink>
            </dd>
          )}
        </dl>
      </td>
      <td className="table__td">
        <div className="fs-n0 lh-sm-md">
          <strong>
            <PluralBlock count={skuCount}>
              <Quantity value={skuCount} /> {singleSKUStatusName.toLowerCase()}{' '}
              <Plural word="SKU" />
            </PluralBlock>
          </strong>
        </div>
        {isExpanded && lineCountList.length > 1 && (
          <div>
            <ul className="list list--square">
              {lineCountList.map(({display, value, count}) => (
                <li className="fs-n0 lh-sm margin-top-7" key={value}>
                  {display}: <Quantity value={count} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </td>

      <td className="table__td">
        <div>
          <strong className="fs-n0 lh-md">{getWarehouseName(warehouse)}</strong>
        </div>
        <div className="wrap--po-addl-info divider--top">
          <RowAddress
            address={getWarehouseAddress(warehouse)}
            noPhone
            noEmail
          />
        </div>
      </td>
      <td className="table__td">
        <div className="fs-n0 lh-md margin-bottom-1">
          <FormattedDate value={createdDate} format="MMM D, YYYY" />
          {' at '}
          <FormattedDate value={createdDate} format="h:mma" />
        </div>
        <em className="fs-n2 text--lt-grey">
          Updated: <FormattedDate value={updatedDate} format="MMM D, YYYY" />
          {' at '}
          <FormattedDate value={updatedDate} format="h:mma" />
        </em>
      </td>

      <td className="table__td align-right">
        <ButtonLink
          className="no-underline btn--expand-product-row"
          onClick={() => stocktakeListToggleExpanded(referenceID)}
        >
          <Icon name="expand" className="fs-01 lh-0 v-align-middle" />
        </ButtonLink>
      </td>
    </tr>
  )
}
